import { SALES_EMAIL } from "@cms/config";
import React from "react";
import { useOutletContext } from "react-router-dom";
import { Mail, Check, UtensilsCrossed, CalendarClock, Puzzle } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@cms/ui/components/card";
import { Badge } from "@cms/ui/components/badge";
import { Button } from "@cms/ui/components/button";
import { PageHeader } from "@cms/ui/components/page-header";
import { MODULE_CATALOGUE } from "../lib/modules";
import { formatMonthlyPence } from "@cms/portal-lib/format";
import type { ClientOutletContext } from "../lib/outlet-context";

type Module = (typeof MODULE_CATALOGUE)[number];

// Where "Ask us to add this" lands. A sales touchpoint, not a checkout —
// the founder confirms the payment method and activates via the admin panel.

const moduleIcons: Record<string, LucideIcon> = {
  restaurant_menu: UtensilsCrossed,
  booking: CalendarClock,
};

function enquiryMailto(module: Module, tenantId: string) {
  const subject = `Add the ${module.label} module to my site`;
  const body = [
    "Hi,",
    "",
    `I'd like to add the ${module.label} module (${formatMonthlyPence(module.monthly_pence)}) to my website.`,
    "",
    `Site: ${tenantId}`,
  ].join("\n");
  return `mailto:${SALES_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function ModulesPage() {
  const { activeModules, tenantId } = useOutletContext<ClientOutletContext>();
  const active = Array.isArray(activeModules) ? activeModules : [];

  return (
    <div>
      <PageHeader
        title="Add-ons"
        description="Optional modules that add new sections to your site and portal. Ask us to add one and we'll set it up on your next bill."
      />

      <div className="grid max-w-3xl gap-4 sm:grid-cols-2">
        {MODULE_CATALOGUE.map((module) => {
          const Icon = moduleIcons[module.slug] ?? Puzzle;
          const isActive = active.includes(module.slug);
          return (
            <Card key={module.slug}>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {module.label}
                </CardTitle>
                {isActive ? (
                  <Badge variant="success" className="gap-1">
                    <Check className="h-3 w-3" />
                    Active
                  </Badge>
                ) : (
                  <Badge variant="secondary">{formatMonthlyPence(module.monthly_pence)}</Badge>
                )}
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">{module.description}</CardDescription>
                {isActive ? (
                  <p className="text-xs text-muted-foreground">
                    Included in your plan — manage it from the sidebar.
                  </p>
                ) : (
                  <Button size="sm" variant="outline" asChild>
                    <a href={enquiryMailto(module, tenantId)}>
                      <Mail className="h-4 w-4" />
                      Ask us to add this
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="mt-6 max-w-3xl text-xs text-muted-foreground">
        No self-serve checkout here on purpose — email us and we'll confirm the
        price, add it to your monthly Direct Debit, and switch it on for you.
      </p>
    </div>
  );
}
